import { Editor } from '@monaco-editor/react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Text
} from '@sparrowengg/twigs-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SaveIcon,
  TickCircleIcon
} from '@sparrowengg/twigs-react-icons';
import { Spinner } from 'commons/components/spinner';
import ActionEnergy from 'commons/icons/action-energy';
import { devPortalServices } from 'commons/services';
import { SingleEventType } from 'features/apps/add-apps/types';
import { AppEventConfigurationProps } from 'features/webhook/types';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const AppEventConfiguration = ({
  previousHandler,
  handleBtnLoader,
  btnLoader,
  setValue,
  getValues
}: AppEventConfigurationProps) => {
  const [currentEvents, setCurrentEvents] = useState<any>([]);
  const [loader, setLoader] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<any>({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const previousEvents = getValues('events');
  const getAllEvents = async () => {
    try {
      let response = (await devPortalServices.getAllWebhookEvents())?.data;
      if (queryParams?.get('id')) {
        response = response?.map((currentEvent: any) => ({
          ...currentEvent,
          events: currentEvent?.events?.map((event: SingleEventType) => {
            if (
              previousEvents.filter(
                (previousEvent: SingleEventType) =>
                  previousEvent.name === event.name
              ).length
            ) {
              return {
                ...event,
                isChecked: true
              };
            } else {
              return event;
            }
          })
        }));
      }

      setCurrentEvents(response);
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const eventSelectHandler = (
    value: boolean,
    eventId: number,
    currentEventId: number,
    selectedEvent: any
  ) => {
    const updatedCurrentEvents = currentEvents.map((event: any) =>
      currentEventId === event.id
        ? {
            ...event,
            events: [
              ...event?.events?.map((subEvent: any) =>
                subEvent?.id === eventId
                  ? { ...subEvent, isChecked: value }
                  : subEvent
              )
            ]
          }
        : event
    );
    if (
      previousEvents?.find((event: any) => event.name === selectedEvent.name)
    ) {
      setValue(
        'events',
        previousEvents?.filter(
          (event: any) => event.name !== selectedEvent.name
        ),
        { shouldDirty: true }
      );
    } else if (previousEvents?.length) {
      setValue('events', [...previousEvents, selectedEvent], {
        shouldDirty: true
      });
    } else {
      setValue('events', [selectedEvent], { shouldDirty: true });
    }
    setCurrentEvents(updatedCurrentEvents);
  };

  useEffect(() => {
    getAllEvents();
  }, []);

  return (
    <>
      {loader ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ height: 'calc(100vh - 101px - 60px)' }}
        >
          <Spinner size="md" />
        </Flex>
      ) : (
        <Flex justifyContent="center" flexDirection="column">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            css={{ position: 'relative' }}
          >
            <Flex flexDirection="column" gap="$2">
              <Heading size="h5">Event Subscriptions</Heading>
              <Text size="md" css={{ color: '$neutral800' }}>
                Choose the events to continue
              </Text>
            </Flex>
            <Button
              rightIcon={<SaveIcon />}
              size="lg"
              type="submit"
              onClick={() => handleBtnLoader()}
              loading={btnLoader}
              disabled={
                !currentEvents.some(
                  (currentEvent: any) =>
                    currentEvent?.events?.some((event: any) => event?.isChecked)
                )
              }
            >
              Save Webhook
            </Button>
            <IconButton
              icon={<ChevronLeftIcon />}
              size="lg"
              variant="ghost"
              css={{ position: 'absolute', left: '-$30', top: 0 }}
              onClick={() => previousHandler()}
            />
          </Flex>
          <Flex justifyContent="center" gap="$20" css={{ marginTop: '$24' }}>
            <Box
              css={{
                flex: 1
              }}
            >
              <Flex
                flexDirection="column"
                gap="$4"
                css={{
                  overflowY: 'auto',
                  height: 'calc(100vh - 213px - 96px)',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}
              >
                {currentEvents?.map((currentEvent: any) => {
                  const count = currentEvent.events.filter(
                    (event: any) => event?.isChecked
                  ).length;
                  return (
                    <EventItem
                      count={count}
                      currentEvent={currentEvent}
                      eventSelectHandler={eventSelectHandler}
                      handleSelectedEvent={(value: any) =>
                        setSelectedEvent(value)
                      }
                    />
                  );
                })}
              </Flex>
            </Box>
            <Box
              css={{
                flex: 1
              }}
            >
              <Box
                css={{
                  width: 624,
                  borderRadius: '$lg',
                  background: '$secondary700',
                  color: '$white900'
                }}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  css={{
                    padding: '$12',
                    borderBottom:
                      '$borderWidths$xs solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <Text size="md" weight="bold">
                    Sample Event Payload (JSON)
                  </Text>
                  <Flex alignItems="center" gap="$4">
                    <Box
                      css={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '$pill',
                        background: '$primary400'
                      }}
                    />
                    <Text size="sm" weight="medium">
                      {selectedEvent?.name ?? 'No event selected'}
                    </Text>
                  </Flex>
                </Flex>
                <Box
                  css={{
                    height: 416,
                    overflow: 'auto',
                    background: '#444B5A',
                    borderRadius: '$md',
                    padding: '$5'
                  }}
                >
                  {!!selectedEvent?.name ? (
                    <Editor
                      height="100%"
                      width="100%"
                      defaultLanguage="json"
                      value={JSON.stringify(selectedEvent?.payload, null, 2)}
                      options={{
                        fontFamily: 'monospace',
                        readOnly: true,
                        lineNumbers: 'off',
                        scrollbar: {
                          vertical: 'hidden',
                          horizontal: 'hidden'
                        },
                        minimap: {
                          enabled: false
                        }
                      }}
                      beforeMount={(monaco) => {
                        monaco.editor.defineTheme('custom-theme', {
                          base: 'vs-dark',
                          inherit: true,
                          rules: [],
                          colors: {
                            'editor.background': '#444B5A',
                            'editor.lineHighlightBackground': '#444B5A',
                            'editorGutter.background': '#444B5A'
                          }
                        });
                      }}
                      onMount={(editor, monaco) => {
                        monaco.editor.setTheme('custom-theme');
                        editor.updateOptions({
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontLigatures: true,
                          lineHeight: 20,
                          readOnly: true
                        });
                      }}
                    />
                  ) : (
                    <Text size="sm" css={{ padding: '$6' }}>
                      Please click on an event to view the sample event payload
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default AppEventConfiguration;

const EventItem = ({
  currentEvent,
  count,
  eventSelectHandler,
  handleSelectedEvent
}: any) => {
  const [isActive, setIsActive] = useState(false);
  return (
    <Flex flexDirection="column">
      <Flex
        justifyContent="space-between"
        css={{
          padding: '$4',
          cursor: 'pointer',
          borderRadius: '$lg'
        }}
        onClick={() => {
          setIsActive(!isActive);
        }}
      >
        <Flex alignItems="center" gap="$2">
          <ActionEnergy color="#64748B" size={16} />
          <Text size="md">{currentEvent?.name}</Text>
        </Flex>
        <Flex alignItems="center">
          <Flex
            alignItems="center"
            gap="$2"
            css={{
              background: '$primary100',
              padding: '3px',
              borderRadius: '$pill',
              height: '$5'
            }}
          >
            <TickCircleIcon size={14} color="#3B8088" />
            <Text size="sm" css={{ color: '$primary700' }}>
              {count}
            </Text>
          </Flex>
          <Box
            css={{
              transform: `rotate(${isActive ? '90deg' : '0deg'})`,
              transition: 'all .3s ease'
            }}
          >
            <ChevronRightIcon
              size={24}
              color={isActive ? '#56B0BB' : '#6A6A6A'}
            />
          </Box>
        </Flex>
      </Flex>
      {isActive ? (
        <Flex flexDirection="column" css={{ paddingLeft: '$12' }} gap="$2">
          {currentEvent?.events?.map((event: any) => {
            return (
              <Flex
                key={event?.id}
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleSelectedEvent(event)}
                css={{
                  padding: '$4',
                  cursor: 'pointer',
                  borderRadius: '$lg',
                  background: event?.isChecked ? '$primary50' : 'transparent',
                  '&:hover': {
                    background: '$primary50'
                  }
                }}
              >
                <Flex
                  alignItems="center"
                  gap="$4"
                  css={{
                    button: {
                      border: '$borderWidths$xs solid $primary400 !important',
                      '&[data-state="checked"]': {
                        background: '$primary500 !important'
                      }
                    }
                  }}
                >
                  <Checkbox
                    size="sm"
                    checked={event?.isChecked}
                    css={{ cursor: 'pointer' }}
                    onChange={(value) =>
                      eventSelectHandler(value, event?.id, currentEvent?.id, {
                        ...(() => {
                          const {
                            description,
                            internal,
                            payload,
                            ...updatedEvent
                          } = event;
                          return updatedEvent;
                        })()
                      })
                    }
                  />
                  <Text size="sm">{event.name}</Text>
                </Flex>
                <ChevronRightIcon
                  size={24}
                  color={event?.isChecked ? '#56B0BB' : '#6A6A6A'}
                />
              </Flex>
            );
          })}
        </Flex>
      ) : null}
    </Flex>
  );
};
